import { request } from './_core'

// ── Printables ───────────────────────────────────────────────

export type PrintableType = 'memory_cards' | 'memory_reference' | 'definitions' | 'anagram'
export type PrintableGenerationStatus = 'PENDING' | 'GENERATING' | 'DONE' | 'FAILED'

export interface PrintableSummary {
  id:               number
  title:            string
  activitySlug:     string
  type:             PrintableType
  themeId:          number | null
  difficulty:       string
  language:         string
  parameters:       Record<string, unknown> | null
  requiredTier:     'BASIC' | 'PLUS' | 'PREMIUM'
  published:        boolean
  generationStatus: PrintableGenerationStatus
  generationError:  string | null
  hasFile:          boolean
  groupId:          string | null
  createdAt:        string
  updatedAt:        string
  locked?:          boolean
}

export interface AdminPrintableSummary extends PrintableSummary {
  themeName:  { es: string; en: string } | null
  themeCover: string | null
}

export type RandomNotebookSpec =
  | { type: 'wordsearch'; themeIds: number[]; difficulty: string; mode?: string; count: number }
  | { type: 'sudoku';     difficulty: string; mode?: string; count: number }
  | { type: 'maths';      operation: string;  difficulty: string; count: number }
  | { type: 'attention';  difficulty: string; count: number; themeIds?: number[]; mode?: string }
  | { type: 'symmetry';      difficulty: string; mode: string; count: number }
  | { type: 'whats_missing'; difficulty: string; mode: string; count: number }
  | { type: 'roman';         difficulty: string; count: number }
  | { type: 'hours';         difficulty: string; clockType: string; writeMode: string; count: number }
  | { type: 'synonyms';      gameMode: string; printMode: string; count: number }

export type NotebookItem =
  | { id: number; sortOrder: number; itemType: 'printable'; printableId: number; title: string; type: PrintableType }
  | { id: number; sortOrder: number; itemType: 'random';    spec: RandomNotebookSpec }
  | { id: number; sortOrder: number; itemType: 'per-theme'; spec: { type: PrintableType; themeIds: number[]; language: string; mode?: string } }

export type NotebookItemInput =
  | { itemType: 'random'; spec: RandomNotebookSpec }

export interface NotebookSummary {
  id:               number
  title:            string
  language:         string
  groupId:          string | null
  requiredTier:     'BASIC' | 'PLUS' | 'PREMIUM'
  published:        boolean
  generationStatus: PrintableGenerationStatus
  generationError:  string | null
  hasFile:          boolean
  createdAt:        string
  updatedAt:        string
  locked?:          boolean
  itemCount?:       number
  pageCount?:       number
  activities?:      string[]
  items?:           NotebookItem[]
}

export const printables = {
  // User-facing
  list:     ()         => request<(PrintableSummary & { locked: boolean })[]>('/printables'),
  download: (id: number) => request<{ url: string }>(`/printables/${id}/download`),

  // Admin
  adminList: ()        => request<AdminPrintableSummary[]>('/printables/admin'),
  create: (data: {
    titleEs?: string; titleEn?: string
    activitySlug: string
    themeId?: number; requiredTier: 'BASIC' | 'PLUS' | 'PREMIUM'
    mode?: 'definitions' | 'anagram'
  }) => request<{ es: AdminPrintableSummary; en: AdminPrintableSummary }>('/printables', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: Partial<{
    title: string; difficulty: string; language: string
    parameters: Record<string, unknown>; requiredTier: 'BASIC' | 'PLUS' | 'PREMIUM'; published: boolean
  }>) => request<PrintableSummary>(`/printables/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) => request<void>(`/printables/${id}`, { method: 'DELETE' }),
  generate: (id: number) => request<{ id: number; generationStatus: string }>(`/printables/${id}/generate`, { method: 'POST' }),
  preview:  (id: number) => request<{ url: string }>(`/printables/${id}/preview`),
}

export const notebooks = {
  // User-facing
  list:     ()         => request<(NotebookSummary & { locked: boolean })[]>('/notebooks'),
  download: (id: number) => request<{ url: string }>(`/notebooks/${id}/download`),

  // Admin
  adminList: ()        => request<NotebookSummary[]>('/notebooks/admin'),
  create: (data: { titleEs?: string; titleEn?: string; requiredTier: 'BASIC' | 'PLUS' | 'PREMIUM'; items: NotebookItemInput[] }) =>
    request<{ es: NotebookSummary; en: NotebookSummary }>('/notebooks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: Partial<{
    title: string; language: string; requiredTier: 'BASIC' | 'PLUS' | 'PREMIUM'
    published: boolean; items: NotebookItemInput[]
  }>) => request<NotebookSummary>(`/notebooks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) => request<void>(`/notebooks/${id}`, { method: 'DELETE' }),
  generate: (id: number) => request<{ id: number; generationStatus: string }>(`/notebooks/${id}/generate`, { method: 'POST' }),
  preview:  (id: number) => request<{ url: string }>(`/notebooks/${id}/preview`),
}

export const userNotebooks = {
  list: () => request<NotebookSummary[]>('/notebooks/my'),
  create: (data: {
    title:    string
    language: string
    items: Array<
      | { type: 'wordsearch'; difficulty: string; count: number; mode?: string; themeIds?: number[] }
      | { type: 'sudoku';     difficulty: string; count: number; mode?: string }
      | { type: 'maths';      difficulty: string; count: number; operation: string }
      | { type: 'attention';  difficulty: string; count: number; themeIds?: number[]; mode?: string }
      | { type: 'symmetry';      difficulty: string; count: number; mode: string }
      | { type: 'whats_missing'; difficulty: string; count: number; mode: string }
      | { type: 'symmetry';   difficulty: string; count: number; mode: string }
      | { type: 'roman';      difficulty: string; count: number }
      | { type: 'hours';      difficulty: string; count: number; clockType: string; writeMode: string }
      | { type: 'synonyms';   difficulty: string; count: number; gameMode: string; printMode: string }
    >
  }) => request<NotebookSummary>('/notebooks/my', { method: 'POST', body: JSON.stringify(data) }),
  download: (id: number) => request<{ url: string }>(`/notebooks/my/${id}/download`),
  generate: (id: number) => request<{ id: number; generationStatus: string }>(`/notebooks/my/${id}/generate`, { method: 'POST' }),
  delete:   (id: number) => request<void>(`/notebooks/my/${id}`, { method: 'DELETE' }),
}
